import { useState, useEffect } from 'react';
import {
  Fade,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  InputAdornment,
  TablePagination,
  CircularProgress,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
// import * as XLSX from 'xlsx-js-style';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import ForecastModal from './ForecastModal';

function StockReportIndex() {
  useSessionValidation();

  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated, companyId, branchId, logout, token, username } = useAuth();

  // Form states
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [items, setItems] = useState([]);
  const [stockData, setStockData] = useState([]);

  // Company and Branch details for Excel export
  const [companyDetails, setCompanyDetails] = useState(null);
  const [branchDetails, setBranchDetails] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Forecast modal states
  const [forecastModalOpen, setForecastModalOpen] = useState(false);
  const [selectedForecastItem, setSelectedForecastItem] = useState(null);

  useEffect(() => {
    setMounted(true);
    if (isAuthenticated && tokenIsExpired(token)) {
      logout();
      return;
    }
  }, [isAuthenticated, companyId]);

  useEffect(() => {
    if (companyId && token) {
      fetchItemsData();
      fetchCompanyDetails();
      fetchBranchDetails();
    }
  }, [companyId, token]);

  const fetchItemsData = async () => {
    try {
      const response = await fetchData('ItemApi', `/${companyId}`, token);
      if (response && response.responseCode === '1111' && response.response_data) {
        setItems(response.response_data);
      } else {
        setItems([]);
      }
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
    }
  };

  const fetchCompanyDetails = async () => {
    try {
      const response = await fetchData('CompanyApi', `/${companyId}`, token);
      if (response && response.responseCode === '1111') {
        const data = response.response_data || response.data || response;
        setCompanyDetails(Array.isArray(data) ? data[0] : data);
      }
    } catch (error) {
      console.error('Error fetching company details:', error);
    }
  };

  const fetchBranchDetails = async () => {
    try {
      const response = await fetchData('BranchApi', `/${branchId}`, token);
      if (response && response.responseCode === '1111') {
        const data = response.response_data || response.data || response;
        setBranchDetails(Array.isArray(data) ? data[0] : data);
      }
    } catch (error) {
      console.error('Error fetching branch details:', error);
    }
  };

  const fetchStockReport = async () => {
    if (!toDate) {
      alert('Please select To Date');
      return;
    }

    setLoading(true);
    try {
      const itemId = selectedItemId || 0; // Use 0 for all items
      const params = `/stock/${toDate}/${companyId}/${itemId}`;
      
      const response = await fetchData('ReportApi', params, token);
      
      if (response && response.responseCode === '1111' && response.response_data) {
        setStockData(response.response_data);
      } else {
        setStockData([]);
      }
    } catch (error) {
      console.error('Error fetching stock report:', error);
      setStockData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleForecastClick = (item) => {
    setSelectedForecastItem(item);
    setForecastModalOpen(true);
  };

  const handleCloseForecastModal = () => {
    setForecastModalOpen(false);
    setSelectedForecastItem(null);
  };

  // Search handler
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  // Filter data based on search term
  const filteredData = stockData.filter((item) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      item.Name?.toLowerCase().includes(searchLower) ||
      item.ItemCode?.toLowerCase().includes(searchLower)
    );
  });

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Calculate pagination values
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  // Calculate totals
  const calculateTotals = () => {
    return filteredData.reduce((acc, item) => ({
      opening: acc.opening + (parseFloat(item.OpeningStock) || 0),
      purchase: acc.purchase + (parseFloat(item.PurchaseQty) || 0),
      sale: acc.sale + (parseFloat(item.SaleQty) || 0),
      onRent: acc.onRent + (parseFloat(item.OnRentQty) || 0),
      forecast: acc.forecast + (parseFloat(item.ForecastQty) || 0),
      balance: acc.balance + (parseFloat(item.BalanceQty) || 0)
    }), { opening: 0, purchase: 0, sale: 0, onRent: 0, forecast: 0, balance: 0 });
  };

  const totals = calculateTotals();

  // Excel export function with advanced styling
  const exportToExcel = async () => {
    if (filteredData.length === 0) {
      alert("No data to export");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Stock Report");

    // Get company and branch names
    const companyName = companyDetails?.CompanyName || companyDetails?.Name || 'N/A';
    const branchName = branchDetails?.BranchName || branchDetails?.Name || 'N/A';

    // Add Company and Branch on same line
    const companyBranchRow = worksheet.addRow(['Company:', companyName, '|', 'Branch:', branchName]);
    companyBranchRow.font = { bold: true, size: 12 };
    companyBranchRow.getCell(1).font = { bold: true };
    companyBranchRow.getCell(4).font = { bold: true };

    // Add Date Range, Generated By, and Generated Time on same line
    const currentDateTime = new Date().toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
    const infoRow = worksheet.addRow([
      'Date Range:', 
      `To Date: ${toDate}`,
      '|',
      'Generated By:',
      username || 'N/A',
      '|',
      'Generated Time:',
      currentDateTime
    ]);
    infoRow.font = { bold: true, size: 12 };
    infoRow.getCell(1).font = { bold: true };
    infoRow.getCell(4).font = { bold: true };
    infoRow.getCell(7).font = { bold: true };

    // Add empty row
    worksheet.addRow([]);

    // Add Header Row
    const headerRow = worksheet.addRow([
      "SL No",
      "Item",
      "Opening Stock",
      "Purchase Qty",
      "Sale Qty",
      "On Rent Qty",
      "Balance Qty",
      "Forecast Qty"
    ]);

    headerRow.font = { bold: true };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FF667eea" }
    };
    headerRow.font = { color: { argb: "FFFFFFFF" }, bold: true };

    // Add Data Rows
    filteredData.forEach((row, index) => {
      worksheet.addRow([
        index + 1,
        row.Name,
        row.OpeningStock || 0,
        row.PurchaseQty || 0,
        row.SaleQty || 0,
        row.OnRentQty || 0,
        row.BalanceQty || 0,
        row.ForecastQty || 0
      ]);
    });

    // Set column widths
    worksheet.columns = [
      { width: 10 },  // SL No
      { width: 35 },  // Item
      { width: 15 },  // Opening Stock
      { width: 15 },  // Purchase Qty
      { width: 15 },  // Sale Qty
      { width: 15 },  // On Rent Qty
      { width: 15 },  // Balance Qty
      { width: 15 }   // Forecast Qty
    ];

    // Add Total Row
    const totalRow = worksheet.addRow([
      "",
      "TOTAL",
      totals.opening,
      totals.purchase,
      totals.sale,
      totals.onRent,
      totals.balance,
      totals.forecast
    ]);
    totalRow.font = { bold: true };

    const buffer = await workbook.xlsx.writeBuffer();
    const fileName = `Stock_Report_${toDate}_${new Date().toISOString().split('T')[0]}.xlsx`;
    saveAs(new Blob([buffer]), fileName);
  };

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{
        minWidth: '100%',
        background: '#f5f7fa',
        padding: '10px',
        position: 'relative'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          padding: '12px',
          position: 'relative',
          color: 'black',
          fontFamily: 'Times New Roman',
        }}>
          {/* Header Section */}
          <div style={{
            marginBottom: '12px',
            paddingBottom: '8px',
            borderBottom: '1px solid #f0f0f0'
          }}>
            <h4 style={{
              fontSize: '17px',
              fontWeight: '700',
              margin: 0,
              letterSpacing: '-0.3px',
              fontFamily: 'Times New Romans',
              textTransform: 'uppercase',
              color: 'black'
            }}>
              Stock Report
            </h4>
          </div>

          {/* Filter Section */}
          <div style={{ 
            marginBottom: '12px', 
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}>
            <div style={{ 
              display: 'flex', 
              gap: '8px', 
              flexWrap: 'nowrap',
              minWidth: 'fit-content',
              alignItems: 'flex-end'
            }}>
              <div style={{ flexShrink: 0 }}>
                <div style={{ fontSize: '10px', color: '#666', marginBottom: '2px', paddingLeft: '4px' }}>To Date</div>
                <TextField
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  size="small"
                  sx={{
                    width: '140px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '11px',
                      height: '28px',
                      '&:hover fieldset': {
                        borderColor: '#667eea',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                  }}
                />
              </div>

              <div style={{ flexShrink: 0 }}>
                <div style={{ fontSize: '10px', color: '#666', marginBottom: '2px', paddingLeft: '4px' }}>Item (Optional)</div>
                <TextField
                  select
                  value={selectedItemId}
                  onChange={(e) => setSelectedItemId(e.target.value)}
                  size="small"
                  SelectProps={{ native: true }}
                  sx={{
                    width: '300px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '11px',
                      height: '28px',
                      '&:hover fieldset': {
                        borderColor: '#667eea',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                  }}
                >
                  <option value="">All Items</option>
                  {items.map(item => (
                    <option key={item.Id} value={item.Id}>
                      {item.Code} - {item.Name}
                    </option>
                  ))}
                </TextField>
              </div>

              <TextField
                placeholder="Search by item name or code..."
                value={searchTerm}
                onChange={handleSearchChange}
                size="small"
                sx={{
                  flexGrow: 1,
                  minWidth: '200px',
                  flexShrink: 0,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    backgroundColor: '#f8f9fa',
                    fontSize: '11px',
                    height: '28px',
                    '&:hover fieldset': {
                      borderColor: '#667eea',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#667eea',
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: '#667eea', fontSize: '14px' }} />
                    </InputAdornment>
                  )
                }}
              />

              <Button
                variant="contained"
                onClick={fetchStockReport}
                disabled={loading}
                startIcon={<RefreshIcon style={{ fontSize: '14px' }} />}
                style={{
                  background: 'linear-gradient(135deg, #11998e 0%, #042812ff 100%)',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: '600',
                  boxShadow: '0 2px 6px rgba(17, 153, 142, 0.3)',
                  border: 'none',
                  height: '28px',
                  textTransform: 'none',
                  letterSpacing: '0.3px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                {loading ? 'Loading...' : 'Refresh'}
              </Button>

              {stockData.length > 0 && (
                <Button
                  variant="contained"
                  startIcon={<FileDownloadIcon style={{ fontSize: '14px' }} />}
                  onClick={exportToExcel}
                  disabled={filteredData.length === 0}
                  style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '6px',
                    fontSize: '11px',
                    fontWeight: '600',
                    boxShadow: '0 2px 6px rgba(102, 126, 234, 0.3)',
                    border: 'none',
                    height: '28px',
                    textTransform: 'none',
                    letterSpacing: '0.3px',
                    whiteSpace: 'nowrap',
                    flexShrink: 0
                  }}
                >
                  Export Excel
                </Button>
              )}
            </div>
          </div>

          {/* Table */}
          {stockData.length > 0 && !loading && (
            <>
              <div style={{ marginBottom: '10px' }}>
                <TableContainer component={Paper} style={{
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                  borderRadius: '10px',
                  overflow: 'hidden'
                }}>
                  <Table sx={{ minWidth: 650 }} aria-label="stock report table">
                    <TableHead>
                      <TableRow style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      }}>
                        {["SL No", "Item", "Opening", "Purchase", "Sale", "On Rent", "Balance", "Forecast"].map((head, idx) => (
                          <TableCell key={head} align={idx === 0 ? "center" : (idx === 1 ? "left" : "right")} style={{
                            color: 'white',
                            fontWeight: '600',
                            fontSize: '11px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            padding: '4px 8px',
                            height: '10px'
                          }}>
                            {head}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedData.map((item, index) => (
                        <TableRow key={index} style={{
                          background: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                          borderBottom: '2px solid #f0f0f0'
                        }}>
                          <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', textAlign: 'center', fontWeight: '600' }}>
                            {startIndex + index + 1}
                          </TableCell>
                          <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                            {item.Name}
                          </TableCell>
                          <TableCell align="right" style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                            {item.OpeningStock || 0}
                          </TableCell>
                          <TableCell align="right" style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                            {item.PurchaseQty || 0}
                          </TableCell>
                          <TableCell align="right" style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                            {item.SaleQty || 0}
                          </TableCell>
                          <TableCell align="right" style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                            {item.OnRentQty || 0}
                          </TableCell>
                          <TableCell align="right" style={{
                            fontSize: '12px',
                            padding: '8px 12px',
                            color: '#2d3748',
                            fontWeight: '600'
                          }}>
                            {item.BalanceQty || 0}
                          </TableCell>
                          <TableCell align="right" style={{ fontSize: '12px', padding: '8px 12px' }}>
                            <Tooltip title="View Forecast Details" arrow>
                              <Button
                                size="small"
                                onClick={() => handleForecastClick(item)}
                                style={{
                                  minWidth: 'auto',
                                  padding: '0px 6px',
                                  fontSize: '12px',
                                  color: '#667eea',
                                  textDecoration: 'underline',
                                  textTransform: 'none',
                                  fontWeight: '600'
                                }}
                              >
                                {item.ForecastQty || 0}
                              </Button>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Total Row */}
                      <TableRow style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderTop: '2px solid #764ba2'
                      }}>
                        <TableCell style={{
                          fontSize: '12px',
                          padding: '8px 12px',
                          color: 'white',
                          fontWeight: '700',
                          textAlign: 'center'
                        }}>
                        </TableCell>
                        <TableCell style={{
                          fontSize: '12px',
                          padding: '8px 12px',
                          color: 'white',
                          fontWeight: '700'
                        }}>
                          TOTAL
                        </TableCell>
                        <TableCell align="right" style={{
                          fontSize: '12px',
                          padding: '8px 12px',
                          color: 'white',
                          fontWeight: '700'
                        }}>
                          {totals.opening}
                        </TableCell>
                        <TableCell align="right" style={{
                          fontSize: '12px',
                          padding: '8px 12px',
                          color: 'white',
                          fontWeight: '700'
                        }}>
                          {totals.purchase}
                        </TableCell>
                        <TableCell align="right" style={{
                          fontSize: '12px',
                          padding: '8px 12px',
                          color: 'white',
                          fontWeight: '700'
                        }}>
                          {totals.sale}
                        </TableCell>
                        <TableCell align="right" style={{
                          fontSize: '12px',
                          padding: '8px 12px',
                          color: 'white',
                          fontWeight: '700'
                        }}>
                          {totals.onRent}
                        </TableCell>
                        <TableCell align="right" style={{
                          fontSize: '12px',
                          padding: '8px 12px',
                          color: 'white',
                          fontWeight: '700'
                        }}>
                          {totals.balance}
                        </TableCell>
                        <TableCell align="right" style={{
                          fontSize: '12px',
                          padding: '8px 12px',
                          color: 'white',
                          fontWeight: '700'
                        }}>
                          {totals.forecast}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </div>

              {/* Pagination */}
              <TablePagination
                component="div"
                count={totalItems}
                page={currentPage - 1}
                onPageChange={(_, newPage) => setCurrentPage(newPage + 1)}
                rowsPerPage={itemsPerPage}
                onRowsPerPageChange={(e) => {
                  setItemsPerPage(parseInt(e.target.value, 10));
                  setCurrentPage(1);
                }}
                sx={{
                  '& .MuiTablePagination-toolbar': {
                    fontSize: '12px'
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontSize: '12px',
                    fontWeight: 600
                  }
                }}
              />
            </>
          )}

          {/* Loading State */}
          {loading && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px 20px',
              gap: '16px'
            }}>
              <CircularProgress size={48} sx={{ color: '#667eea' }} />
              <Typography sx={{ color: '#64748b', fontSize: '14px', fontWeight: 500 }}>
                Loading stock report...
              </Typography>
            </div>
          )}

          {/* No Data Message */}
          {!loading && stockData.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#666',
              fontSize: '12px'
            }}>
              No stock data available. Please select filters and generate report.
            </div>
          )}
        </div>

        {/* Forecast Modal */}
        <ForecastModal
          open={forecastModalOpen}
          onClose={handleCloseForecastModal}
          itemData={selectedForecastItem}
          toDate={toDate}
        />

        {/* Footer */}
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 20px',
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: '16px',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
          fontFamily: 'serif',
          fontWeight: '600',
          color: '#2c3e50',
          fontSize: '13px',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          zIndex: 1000
        }}>
          <span style={{ fontSize: '18px' }}>©️</span>
          <span>EFFTABS SOLUTIONS LLP</span>
        </div>
      </div>
    </Fade>
  );
}

export default StockReportIndex;
